import FileSaver from 'file-saver';
import { defineMessages } from 'react-intl';
import handleHttpError from '@/shared/services/HandleHttpError';
import TranslateService from '@/shared/services/TranslateService';
import adminpulse from '@/shared/utils/adminpulse';
import ToastrService from '@/shared/services/ToastrService';
import config from '../../config';
import api from '../api';

const messages = defineMessages({
  DocumentEditorOpened: 'The document editor will open in a new browser tab.',
  DocumentCouldNotBeOpenedMaybeDeleted:
    'The document could not be downloaded, it might have been deleted.',
  DocumentCheckout:
    'The document is being checked out and will open in a new browser tab or a client app.',
  DocumentCheckin: 'The document is being checked in.',
  DocumentDiscardCheckout: 'The document checkout is being discarded.',
  DiscardCheckout: 'The document checkout is being discarded.',
});

const convertToPdfMimeTypes = [
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.template',
  'application/vnd.ms-word.template.macroenabled.12',
  'application/vnd.ms-word.document.macroenabled.12',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];
let documentTypeInfo = null;

const getDocumentTypeInfo = () => {
  if (documentTypeInfo === null) {
    documentTypeInfo = api.base
      .get(`${config.appUrls.documents}/api/documents/DocumentTypeInfo`)
      .then(({ data }) => data || []);
  }
  return Promise.resolve(documentTypeInfo);
};

const DocumentService = {
  download(id, versionId, filename, mimeType) {
    if (versionId) {
      return api.base
        .post(
          `${config.appUrls.documents}/api/documents/DownloadDocumentLink`,
          {
            documentId: id,
            VersionId: versionId,
            filename,
            mime: mimeType,
          },
          {
            responseType: 'text',
          },
        )
        .then(
          ({ data }) => {
            window.open(data);
          },
          error => {
            if (error.response && error.response.status === 404) {
              ToastrService.error(
                TranslateService.getIntl().formatMessage(
                  messages.DocumentCouldNotBeOpenedMaybeDeleted,
                ),
              );
            } else {
              handleHttpError(TranslateService.getIntl())(error);
            }
          },
        );
    }
    return api.base
      .get(`${config.appUrls.documents}/api/documents/DownloadDocumentLink/${id}`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => {
          window.open(data);
        },
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );

    return api.base
      .get(url, {
        responseType: 'text',
      })
      .then(
        ({ data }) => {
          window.open(data);
        },
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );
  },
  openInApp(id) {
    return api.base
      .get(`${config.appUrls.documents}/api/documents/${id}/openinapp`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => {
          window.open(data, '_blank');
        },
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );
  },
  edit(id, language) {
    ToastrService.info(TranslateService.getIntl().formatMessage(messages.DocumentEditorOpened));
    return api.base
      .get(`${config.appUrls.documents}/api/documents/EditDocumentLink/${id}/${language}`)
      .then(
        ({ data }) => {
          window.open(data);
        },
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },
  checkout(id, openInApp) {
    ToastrService.info(TranslateService.getIntl().formatMessage(messages.DocumentCheckout));
    return api.base
      .post(`${config.appUrls.documents}/api/documents/${id}/checkout?openinApp=${openInApp}`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => {
          window.open(data);
          return data;
        },
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },
  checkin(id) {
    ToastrService.info(TranslateService.getIntl().formatMessage(messages.DocumentCheckin));
    return api.base
      .post(`${config.appUrls.documents}/api/documents/${id}/checkin`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => data,
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },
  discardCheckout(id) {
    ToastrService.info(TranslateService.getIntl().formatMessage(messages.DiscardCheckout));
    return api.base
      .post(`${config.appUrls.documents}/api/documents/${id}/discardcheckout`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => data,
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },
  getTemplateCheckoutInfo(documentIds) {
    return api.base
      .post(`${config.appUrls.documents}/api/documents/checkedoutstatus`, documentIds)
      .then(
        ({ data }) => {
          return data;
        },
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },
  open(id) {
    return api.base
      .get(`${config.appUrls.documents}/api/documents/opendocumentlink/${id}`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => {
          window.open(data);
        },
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );
  },

  getLink(id) {
    return api.base
      .get(`${config.appUrls.documents}/api/documents/opendocumentlink/${id}`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => data,
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );
  },
  getCheckedOutLink(id) {
    return api.base
      .get(`${config.appUrls.documents}/api/documents/checkedoutlink/${id}`, {
        responseType: 'text',
      })
      .then(
        ({ data }) => data,
        error => {
          if (error.response && error.response.status === 404) {
            ToastrService.error(
              TranslateService.getIntl().formatMessage(
                messages.DocumentCouldNotBeOpenedMaybeDeleted,
              ),
            );
          } else {
            handleHttpError(TranslateService.getIntl())(error);
          }
        },
      );
  },
  getDetails(id) {
    return api.base.get(`${config.appUrls.documents}/api/documents/details/${id}`).then(
      ({ data }) => data,
      error => {
        if (error.response && error.response.status === 404) {
          ToastrService.error(
            TranslateService.getIntl().formatMessage(messages.DocumentCouldNotBeOpenedMaybeDeleted),
          );
        } else {
          handleHttpError(TranslateService.getIntl())(error);
        }
      },
    );
  },

  convertToPdf(id, name) {
    return api.base
      .get(`${config.appUrls.documentsConverter}/api/convert/${id}/pdf`, {
        responseType: 'arraybuffer',
      })
      .then(
        ({ data, headers }) => {
          let filename = name;
          if (filename) {
            filename = `${filename.substr(0, filename.lastIndexOf('.'))}.pdf`;
          } else {
            const contentDispositionHeader = headers['content-disposition'];
            filename = adminpulse.getFileNameFromHeader(contentDispositionHeader);
          }
          if (!filename) filename = `${id.substr(0, 8)}.pdf`;

          const mime = headers['content-type'];

          const blob = new Blob([data], {
            type: mime,
          });
          FileSaver.saveAs(blob, filename);
        },
        error => {
          const buffer = error.data || error;
          const errorObject = adminpulse.arrayBufferToObject(buffer) || {};
          handleHttpError(TranslateService.getIntl())(errorObject);
        },
      );
  },

  // converts given documentId to pdf and saves it in document-sql store
  // desired document type can be passed to give new document a specific type, 15 = temporary document
  convertAndSaveToPdf(id, desiredDocumentType = 15) {
    return api.base
      .get(
        `${config.appUrls.documentsConverter}/api/convertandsave/${id}/pdf/${desiredDocumentType}`,
      )
      .then(
        ({ data }) => data,
        error => {
          handleHttpError(TranslateService.getIntl())(error);
        },
      );
  },

  canConvertToPdf(mime) {
    if (!mime || typeof mime !== 'string') return false;
    return convertToPdfMimeTypes.indexOf(mime.toLowerCase()) >= 0;
  },

  getForbiddenDocumentTypes: () => getDocumentTypeInfo().then(({ forbidden = [] }) => forbidden),
  getWithoutRelationDocumentTypes: () =>
    getDocumentTypeInfo().then(({ withoutRelations = [] }) => withoutRelations),

  extractDocumentData(documentId) {
    return api.base.post(`${config.appUrls.api}/api/DocumentExtraction/extract`, {
      documentId,
    });
  },

  // Document types that support AI data extraction
  supportsDataExtraction(documentType) {
    const dataExtractionSupportedTypes = [
      //49, //DepreciationTable disabled until OCR is improved
      //58, //LoanRepaymentTable disabled until OCR is improved
      10, //Eid
      18, //EidPhoto - auto-reclassifies to Eid if eID data is found
      109, //VehicleRegistrationCertificate
      110, //VehicleApplicationForRegistration
      111, //VehicleInvoice
      113, //VehicleDiv
      194, //VehicleTax
      5002, //ContractLoan
      5003, //Contract
    ];

    return dataExtractionSupportedTypes.some(type => type === documentType);
  },
};

export default DocumentService;
